import React from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import passkey from '../../assets/passkey.svg';
import RegistrationLayout from '../../Components/layout/RegistrationLayout';
import RegistrationFormBox from '../../Components/layout/RegistrationFormBox';
import { Box, Button, IconButton, InputAdornment, TextField, Typography } from '@mui/material';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import PasswordStrengthMeter from '../../Components/PasswordStrengthMeter';
import { CognitoUser } from 'amazon-cognito-identity-js';
import userPool from '../../util/cognito/userPool';
import { useNotify } from '../../util/hooks/notify/Notification';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const step = queryParams.get('step') || '1';
  const [email, setEmail] = React.useState('');
  const [code, setCode] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [showPassword, setShowPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);
  const [errors, setErrors] = React.useState({ email: '', code: '', password: '', confirmPassword: '' });
  const notify = useNotify();

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePassword = (password: string) => {
    // eslint-disable-next-line no-useless-escape
    const passwordRegex =  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[.!+,@#$%^&*:;?_|~={}()<>`"'[\]\[\\\/-]).{8,99}$/;
    return passwordRegex.test(password);
  };

  const validateStep = () => {
    let valid = true;
    const newErrors = { email: '', code: '', password: '', confirmPassword: '' };

    if (step === '1') {
      if (!email) {
        newErrors.email = 'Email cím megadása kötelező.';
        valid = false;
      } else if (!validateEmail(email)) {
        newErrors.email = 'Érvénytelen email cím.';
        valid = false;
      }
    }

    if (step === '2' && (!code || code.length !== 6 || isNaN(Number(code)))) {
      newErrors.code = 'A kód 6 jegyű szám kell legyen.';
      valid = false;
    }

    if (step === '3') {
      if (!password) {
        newErrors.password = 'Új jelszó megadása kötelező.';
        valid = false;
      } else if (!validatePassword(password)) {
        newErrors.password = 'A jelszó nem megfelelő.';
        valid = false;
      }
      if (password !== confirmPassword) {
        newErrors.confirmPassword = 'A két jelszónak egyeznie kell.';
        valid = false;
      }
    }

    setErrors(newErrors);
    return valid;
  };

  const handleNextStep = () => {
    if (validateStep()) {
      const nextStep = parseInt(step) + 1;
      navigate(`/forgotPassword?step=${nextStep}`);
    }
  };

  const handleTogglePassword = (param: React.Dispatch<React.SetStateAction<boolean>>) => {
    param(prevState => !prevState);
  };

  const requestNewPassword = () => {
    if (validateStep()) {
    const user = new CognitoUser({ Username: email, Pool: userPool });
    user.forgotPassword({
      onSuccess: (data) => {
        handleNextStep();
      },
      onFailure: (err) => {
        console.error('Error requesting new password:', err);
        setErrors(prevErrors => ({ ...prevErrors, email: 'Hiba történt az új jelszó igénylése során.' }));
      },
    });
    }
  };

  const submitNewPassword = () => {
    if (validateStep()) {
      const user = new CognitoUser({ Username: email, Pool: userPool });
      user.confirmPassword(code, password, {
        onSuccess: () => {
          handleNextStep();
        },
        onFailure: (err) => {
          if (err.message.includes('Invalid verification')) {
            notify('Érvénytelen kód.', 'error');
          }else if (err.message.includes('Password does not conform to policy')) {
            notify('A jelszó nem felel meg a jelszó-házirendnek.', 'error');
          }else{
            notify('A kód lejárt, vagy túl sok próbálkozás történt.', 'error');
          }
        }
      });
    }
  }

  return (
    <RegistrationLayout>
      <RegistrationFormBox text={step === '1' ? 'Elfelejtett jelszó' : step === '2' ? 'Nézd meg az email fiókodat' : 'Új jelszó beállítása'} icon={step === '2' ? <MarkEmailReadIcon sx={{fontSize: '30px'}}/> : <img src={passkey} alt="passkey" style={{width: '30px'}}/>}>
        {step === '1' && (
          <>
            <Typography textAlign={"center"}>Add meg a PinkPOS Admin regisztrációja során használt email címedet, mi pedig elküldjük az új jelszó megadásához szükséges kódot. </Typography>
            <TextField
              fullWidth
              name={'email'}
              type="email"
              label={'Email cím'}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={!!errors.email}
              helperText={errors.email}
            />
            <Button variant={'contained'} color={'primary'} onClick={requestNewPassword}>{'Kérem a kódot'}</Button>
            <Button variant={'outlined'} onClick={() => navigate(-1)}>{"Mégsem"}</Button>
            <hr style={{width: '100%'}}/>
            <Typography variant={'h6'} fontWeight={"lighter"} color={'textSecondary'}>{'Véletlenül kerültél ide?'} </Typography>
            <Button variant={'contained'} sx={{backgroundColor: '#FCD9E8', borderRadius: '30px', textTransform: 'none', fontWeight: 'lighter', color: '#291520'}} onClick={() => navigate('/login')}>{'Vissza a bejelentkezésre'}</Button>
          </>
        )}
        {step === '2' && (
          <>
            <Typography textAlign={'center'}>{"Az általad megadott email címre elküldtük az új jelszó megadásához szükséges kódot."}</Typography>
            <TextField
              fullWidth
              name={'code'}
              type="text"
              label={'Kód'}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              error={!!errors.code}
              helperText={errors.code}
            />
            <Button variant={'contained'} color={'primary'} onClick={handleNextStep}>{'Tovább az új leszó megadásához'}</Button>
            <hr style={{width: '100%'}}/>
            <Typography textAlign={'center'} fontWeight={"lighter"} color={'textSecondary'}>{"Nem kaptad meg az emailt? Ellenőrizd a spam fiókot, vagy"} <Link style={{color: "black"}} to={"/forgotPassword"}>{"próbáld meg egy másik email címmel"}</Link> </Typography>
            <Button variant={'text'} sx={{textTransform: 'none', fontWeight: '300'}} onClick={() => navigate('/login')}>{"Később aktiválom a fiókomat"}</Button>
          </>
        )}
        {step === '3' && (
          <>
            <Typography textAlign={"center"}>Add meg az új jelszavadat.</Typography>
            <Box sx={{width: '100%'}}>
              <TextField
                label="Új jelszó"
                variant="outlined"
                type={showPassword ? 'text' : 'password'}
                fullWidth
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={()=> {handleTogglePassword(setShowPassword)}}
                        edge="end"
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ?  <Visibility /> :<VisibilityOff />  }
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                autoComplete={'off'}
                error={!!errors.password}
                helperText={errors.password}
              />
              <PasswordStrengthMeter password={password}/>
            </Box>
            <Typography>{"A jelszó legyen legalább 8 karakter hosszúságú, kell tartalmaznia kisbetűt, nagybetűt, továbbá számot és speciális karaktert. Nem lehet benne ékezetes betű és szóköz."}</Typography>
            <Box>
              <TextField
                label="Új jelszó megerősítése"
                variant="outlined"
                type={showConfirmPassword ? 'text' : 'password'}
                fullWidth
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={()=> {handleTogglePassword(setShowConfirmPassword)}}
                        edge="end"
                        aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                      >
                        {showConfirmPassword ? <Visibility />  : <VisibilityOff /> }
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                autoComplete={'off'}
                error={!!errors.confirmPassword}
                helperText={errors.confirmPassword}
              />
              <Typography variant={'caption'} color={'textSecondary'}>{"A két jelszónak egyeznie kell"}</Typography>
            </Box>
            <Button variant={'contained'} color={'primary'}  onClick={submitNewPassword}>{'Új jelszó beállítása'}</Button>
          </>
        )}
        {step === '4' && (
          <>
            <Typography textAlign={"center"}>{"Az új jelszó beállítása sikeres volt."}</Typography>
            <Button variant={'contained'} color={'primary'}  onClick={() => navigate('/login')}>{'Bejelentkezés'}</Button>
          </>
        )}
      </RegistrationFormBox>
    </RegistrationLayout>
  );
}

export default ForgotPassword;